import React, { useEffect, useState } from 'react'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Chip from '@material-ui/core/Chip'

import { ERRORS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { api, ConfigUtils } from 'utils'

import {
  disconnectGoogle,
  getGoogleAuthUrl,
  getGoogleStatus,
  GoogleConnectionStatus,
  GoogleStatusResponse,
  GoogleSyncResponse,
  syncGoogleReviews,
} from './googleApi'
import { getAppSettings, patchAppSettings, GetSettingsResponse, AppSettings } from './settingsApi'
import { ReplyTemplatesSettings } from './components/ReplyTemplatesSettings'
import { AutoReplyTemplatesSettings } from './components/AutoReplyTemplatesSettings'

const getSettingsGetter = (responseData: GetSettingsResponse) => responseData?.settings
const getGoogleStatusGetter = (responseData: GoogleStatusResponse) => responseData?.google

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('es-AR') : '—')

const MINUTES_ERROR = 'El valor debe ser un entero mayor a 0'

const parsePositiveMinutesInput = (raw: string): number | null => {
  if (raw === '') return null
  if (/^0/.test(raw)) return null
  const n = Number(raw)
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return null
  return n
}

const ReviewsSettings: React.FC = () => {
  const [{ data: appSettings }, refetchSettings] = useApi<
    GetSettingsResponse,
    AppSettings | undefined
  >(getAppSettings, getSettingsGetter, {
    baseData: undefined,
    deps: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [{ data: googleStatus }, refetchGoogleStatus] = useApi<
    GoogleStatusResponse,
    GoogleConnectionStatus | undefined
  >(getGoogleStatus, getGoogleStatusGetter, {
    baseData: undefined,
    deps: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [patchSettingsApi, savingSettings] = useApiCall<
    {
      reviewCooldownDays?: number
      reviewGoogleMinStars?: number
      reviewEmailDelayMinutes?: number
      reviewPushDelayMinutes?: number
      reviewAlertMaxStars?: number
      reviewAutoReplyMinStars?: number
    },
    GetSettingsResponse
  >(patchAppSettings)

  const [syncApi, syncing] = useApiCall<{ storeId?: number } | void, GoogleSyncResponse>(payload =>
    syncGoogleReviews(payload && 'storeId' in payload ? payload.storeId : undefined),
  )

  const [disconnectApi, disconnecting] = useApiCall<void, { ok: boolean }>(() => disconnectGoogle())

  const [loadingConnect, setLoadingConnect] = useState(false)

  const [cooldownDays, setCooldownDays] = useState(30)
  const [googleMinStars, setGoogleMinStars] = useState(4)
  const [pushDelayMinutes, setPushDelayMinutes] = useState<number | ''>(30)
  const [emailDelayMinutes, setEmailDelayMinutes] = useState<number | ''>(60)
  const [alertMaxStars, setAlertMaxStars] = useState(3)
  const [autoReplyMinStars, setAutoReplyMinStars] = useState(4)
  const [pushDelayError, setPushDelayError] = useState('')
  const [emailDelayError, setEmailDelayError] = useState('')

  useEffect(() => {
    if (appSettings) {
      setCooldownDays(appSettings.reviewCooldownDays ?? 30)
      setGoogleMinStars(appSettings.reviewGoogleMinStars ?? 4)
      setPushDelayMinutes(appSettings.reviewPushDelayMinutes ?? 30)
      setEmailDelayMinutes(appSettings.reviewEmailDelayMinutes ?? 60)
      setAlertMaxStars(appSettings.reviewAlertMaxStars ?? 3)
      setAutoReplyMinStars(appSettings.reviewAutoReplyMinStars ?? 4)
      setPushDelayError('')
      setEmailDelayError('')
    }
  }, [appSettings])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('google') === 'connected') {
      snackbar.show('Google Business Profile conectado correctamente')
      refetchGoogleStatus()
      window.history.replaceState({}, '', `${window.location.pathname}?tab=reviews`)
    }
    if (params.get('google') === 'error') {
      snackbar.show('No se pudo conectar con Google. Intentá de nuevo.')
      window.history.replaceState({}, '', `${window.location.pathname}?tab=reviews`)
    }
  }, [refetchGoogleStatus])

  const handleMinutesChange = (
    raw: string,
    setter: (value: number | '') => void,
    setError: (message: string) => void,
  ) => {
    if (raw === '') {
      setter('')
      setError(MINUTES_ERROR)
      return
    }
    if (/^0/.test(raw)) {
      setError(MINUTES_ERROR)
      return
    }
    const parsed = parsePositiveMinutesInput(raw)
    if (parsed == null) {
      setError(MINUTES_ERROR)
      return
    }
    setError('')
    setter(parsed)
  }

  const handleSaveSettings = async () => {
    const isEcard = ConfigUtils.ecard.enabled
    let pushValid: number | null = null
    if (!isEcard && pushDelayMinutes !== '') {
      pushValid = parsePositiveMinutesInput(String(pushDelayMinutes))
    }
    const emailValid =
      emailDelayMinutes === '' ? null : parsePositiveMinutesInput(String(emailDelayMinutes))
    if ((!isEcard && pushValid == null) || emailValid == null) {
      if (!isEcard && pushValid == null) setPushDelayError(MINUTES_ERROR)
      if (emailValid == null) setEmailDelayError(MINUTES_ERROR)
      snackbar.show(MINUTES_ERROR)
      return
    }
    try {
      await patchSettingsApi({
        reviewCooldownDays: cooldownDays,
        reviewGoogleMinStars: googleMinStars,
        ...(!isEcard && pushValid != null ? { reviewPushDelayMinutes: pushValid } : {}),
        reviewEmailDelayMinutes: emailValid,
        reviewAlertMaxStars: alertMaxStars,
        reviewAutoReplyMinStars: autoReplyMinStars,
      })
      refetchSettings()
      snackbar.show('Configuración guardada')
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleConnectGoogle = async () => {
    setLoadingConnect(true)
    try {
      const { data } = await api.request<{ url: string }>(getGoogleAuthUrl())
      window.location.href = data.url
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      setLoadingConnect(false)
    }
  }

  const handleDisconnectGoogle = async () => {
    try {
      await disconnectApi()
      refetchGoogleStatus()
      snackbar.show('Cuenta de Google desconectada')
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleSyncGoogle = async () => {
    try {
      await syncApi()
      refetchGoogleStatus()
      snackbar.show(
        'Sincronización iniciada en segundo plano. Los resultados se actualizarán al finalizar.',
      )
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const isSyncInProgress = Boolean(googleStatus?.syncInProgress)
  const googleBusy = loadingConnect || syncing || disconnecting || isSyncInProgress

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Cantidad de días entre reviews"
            helperText="Frecuencia permitida para que un usuario vuelva a calificar en el mismo local"
            type="number"
            value={cooldownDays}
            onChange={e => setCooldownDays(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Estrellas mínimas para sugerir Google"
            helperText="Umbral de calificación para disparar la redirección a Google Maps"
            type="number"
            value={googleMinStars}
            onChange={e => setGoogleMinStars(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 5 }}
          />
        </Grid>
        {!ConfigUtils.ecard.enabled && (
          <Grid item xs={12} md={4}>
            <TextField
              label="Tiempo de espera para Notificación Push (minutos)"
              helperText={
                pushDelayError ||
                'Minutos después de la visita para enviar la push que abre el modal'
              }
              type="number"
              value={pushDelayMinutes}
              onChange={e =>
                handleMinutesChange(e.target.value, setPushDelayMinutes, setPushDelayError)
              }
              error={Boolean(pushDelayError)}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <TextField
            label="Minutos antes del mail de recordatorio"
            helperText={
              emailDelayError ||
              (ConfigUtils.ecard.enabled
                ? 'Minutos después de la visita para el mail de solicitud de reseña'
                : 'Minutos después del envío de la Push para el mail de respaldo (si la reseña sigue pendiente)')
            }
            type="number"
            value={emailDelayMinutes}
            onChange={e =>
              handleMinutesChange(e.target.value, setEmailDelayMinutes, setEmailDelayError)
            }
            error={Boolean(emailDelayError)}
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Notificar cuando la puntuación es menor o igual a"
            helperText="Se enviarán alertas para reseñas de 1 a N estrellas"
            type="number"
            value={alertMaxStars}
            onChange={e => setAlertMaxStars(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 5 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Estrellas mínimas para respuesta automática"
            helperText="Reseñas de Google con esta calificación o más se responden automáticamente"
            type="number"
            value={autoReplyMinStars}
            onChange={e => setAutoReplyMinStars(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 5 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={savingSettings}
          >
            Guardar configuración
          </Button>
        </Grid>
      </Grid>

      <Divider style={{ margin: '32px 0 24px' }} />

      <Typography
        variant="h6"
        style={{ fontFamily: 'Montserrat', fontWeight: 700, marginBottom: 8 }}
      >
        Plantillas de respuestas
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Configurá respuestas pre-seteadas para que los operadores puedan responder reseñas de Google
        y reseñas de la plataforma con un solo clic.
      </Typography>
      <ReplyTemplatesSettings />

      <Divider style={{ margin: '32px 0 24px' }} />

      <Typography
        variant="h6"
        style={{ fontFamily: 'Montserrat', fontWeight: 700, marginBottom: 8 }}
      >
        Respuestas Automáticas para Reseñas Positivas
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Plantillas que se publican automáticamente en Google al sincronizar reseñas nuevas con
        calificación suficiente. Se eligen al azar entre las activas.
      </Typography>
      <AutoReplyTemplatesSettings />

      <Divider style={{ margin: '32px 0 24px' }} />

      <Typography
        variant="h6"
        style={{ fontFamily: 'Montserrat', fontWeight: 700, marginBottom: 8 }}
      >
        Google Business Profile
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Conectá la cuenta de Google que administra tus locales. Las reseñas se sincronizan a la base
        de datos y se vinculan a cada local mediante el Google Place ID configurado en Locales.
      </Typography>

      <Box mb={2}>
        {googleStatus?.connected ? (
          <Chip label="Conectado" color="primary" style={{ marginRight: 8 }} />
        ) : (
          <Chip label="No conectado" style={{ marginRight: 8 }} />
        )}
        {googleStatus?.connectedEmail && (
          <Typography variant="body2" component="span">
            {googleStatus.connectedEmail}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="textSecondary">
            Última sincronización
          </Typography>
          <Typography variant="body1">{formatDate(googleStatus?.lastSyncAt)}</Typography>
        </Grid>
        {googleStatus?.lastSyncError && (
          <Grid item xs={12}>
            <Typography variant="caption" color="error">
              Último error: {googleStatus.lastSyncError}
            </Typography>
          </Grid>
        )}
        {isSyncInProgress && (
          <Grid item xs={12}>
            <Typography variant="body2" color="primary" style={{ fontWeight: 500 }}>
              Hay una sincronización en proceso. Por favor, probá en 5 minutos refrescando la página
              para verificar si finalizó.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
        {!googleStatus?.connected ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConnectGoogle}
            disabled={googleBusy}
          >
            Conectar con Google
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSyncGoogle}
              disabled={googleBusy}
            >
              Sincronizar ahora
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDisconnectGoogle}
              disabled={googleBusy}
            >
              Desconectar
            </Button>
          </>
        )}
      </Box>
    </Box>
  )
}

export { ReviewsSettings }
